import React, { createContext, useCallback, useContext, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';

interface ToastState {
  id: number;
  message: string;
  variant: 'default' | 'error';
}

interface ToastContextValue {
  showToast: (message: string, variant?: 'default' | 'error') => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de ToastProvider');
  return ctx;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, variant: 'default' | 'error' = 'default') => {
    const id = Date.now();
    setToast({ id, message, variant });
    setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, 2600);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <Box
          data-testid="toast"
          data-variant={toast.variant}
          sx={{
            position: 'fixed',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: toast.variant === 'error' ? TOKENS.red : TOKENS.ink,
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '999px',
            zIndex: 2000,
            boxShadow: '0 8px 24px rgba(0,0,0,.25)',
          }}
        >
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 500 }}>
            {toast.message}
          </Typography>
        </Box>
      )}
    </ToastContext.Provider>
  );
};
